/**
 * Mock data para Traces (Llamadas)
 * Usado para testing visual cuando el API de Langfuse no está disponible
 */

import type { LangfuseTrace, TraceListResponse } from '@/types/traces';

export const mockTraces: LangfuseTrace[] = [
  {
    id: 'trace-mock-001',
    projectId: 'proj-001',
    name: 'Llamada de consulta general',
    timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 min ago
    environment: 'production',
    tags: ['teruya', 'consulta'],
    bookmarked: false,
    release: 'v1.0.0',
    version: '1.0',
    userId: 'user-123',
    sessionId: 'session-abc-001',
    public: false,
    input: [
      {
        role: 'user',
        content: 'Hola, quisiera información sobre sus servicios de consultoría',
        timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString()
      },
      {
        role: 'assistant',
        content: 'Claro que sí. Ofrecemos servicios de consultoría empresarial especializados en transformación digital, optimización de procesos y estrategia corporativa. ¿En qué área específica está interesado?',
        timestamp: new Date(Date.now() - 1000 * 60 * 29).toISOString()
      },
      {
        role: 'user',
        content: 'Me interesa la transformación digital para mi empresa. Tenemos alrededor de 50 empleados',
        timestamp: new Date(Date.now() - 1000 * 60 * 28).toISOString()
      },
      {
        role: 'assistant',
        content: 'Excelente. Para empresas de ese tamaño, tenemos un programa integral que incluye análisis de procesos actuales, implementación de herramientas digitales y capacitación del personal. ¿Le gustaría agendar una reunión con uno de nuestros consultores especializados?',
        timestamp: new Date(Date.now() - 1000 * 60 * 27).toISOString()
      }
    ],
    output: {
      text: 'Perfecto. Hemos registrado su interés en nuestro programa de transformación digital. Un consultor se pondrá en contacto con usted en las próximas 24 horas para agendar una reunión inicial. ¿Hay algo más en lo que pueda ayudarle?'
    },
    metadata: {
      assistant_name: 'Asistente Teruya',
      call: {
        duration: 180,
        quality: 'good'
      }
    },
    createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
    observations: [],
    scores: [
      {
        name: 'Calidad de respuesta',
        value: 0.92,
        source: 'auto',
        comment: 'Respuesta completa y relevante',
        timestamp: new Date(Date.now() - 1000 * 60 * 25).toISOString()
      },
      {
        name: 'Satisfacción del cliente',
        value: 0.88,
        source: 'auto',
        comment: 'Cliente mostró interés en continuar',
        timestamp: new Date(Date.now() - 1000 * 60 * 25).toISOString()
      }
    ],
    totalCost: 0.0234,
    latency: 2.45,
    htmlPath: '/trace/trace-mock-001',
    externalId: null,
    success: true
  },
  {
    id: 'trace-mock-002',
    projectId: 'proj-001',
    name: 'Llamada de soporte técnico',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
    environment: 'production',
    tags: ['teruya', 'soporte'],
    bookmarked: false,
    release: 'v1.0.0',
    version: '1.0',
    userId: 'user-456',
    sessionId: 'session-abc-002',
    public: false,
    input: [
      {
        role: 'user',
        content: 'Tengo problemas para acceder al portal de clientes',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString()
      },
      {
        role: 'assistant',
        content: 'Lamento que esté teniendo problemas. ¿Puede decirme qué mensaje de error aparece cuando intenta acceder?',
        timestamp: new Date(Date.now() - 1000 * 60 * 120 - 5).toISOString()
      },
      {
        role: 'user',
        content: 'Dice que mi contraseña es incorrecta, pero estoy seguro que es la correcta',
        timestamp: new Date(Date.now() - 1000 * 60 * 115).toISOString()
      }
    ],
    output: {
      text: 'Entiendo. Le voy a enviar un enlace para restablecer su contraseña. Por seguridad, también verificaré que su cuenta no esté bloqueada. ¿Cuál es el correo electrónico asociado a su cuenta?'
    },
    metadata: {
      assistant_name: 'Asistente Teruya',
      call: {
        duration: 120,
        quality: 'excellent'
      }
    },
    createdAt: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 115).toISOString(),
    observations: [],
    scores: [
      {
        name: 'Tiempo de respuesta',
        value: 0.95,
        source: 'auto',
        comment: 'Respuesta rápida y efectiva',
        timestamp: new Date(Date.now() - 1000 * 60 * 115).toISOString()
      }
    ],
    totalCost: 0.0156,
    latency: 1.87,
    htmlPath: '/trace/trace-mock-002',
    externalId: null,
    success: true
  },
  {
    id: 'trace-mock-003',
    projectId: 'proj-001',
    name: 'Llamada de ventas',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(), // 5 hours ago
    environment: 'production',
    tags: ['teruya', 'ventas'],
    bookmarked: true,
    release: 'v1.0.0',
    version: '1.0',
    userId: 'user-789',
    sessionId: 'session-abc-003',
    public: false,
    input: [
      {
        role: 'user',
        content: 'Buenos días, me gustaría cotizar sus servicios de consultoría estratégica',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString()
      },
      {
        role: 'assistant',
        content: '¡Buenos días! Con gusto le ayudo con la cotización. Para brindarle una propuesta personalizada, ¿podría contarme un poco sobre su empresa y los desafíos que buscan resolver?',
        timestamp: new Date(Date.now() - 1000 * 60 * 295).toISOString()
      },
      {
        role: 'user',
        content: 'Somos una empresa de manufactura con 200 empleados. Necesitamos mejorar nuestra eficiencia operativa y reducir costos',
        timestamp: new Date(Date.now() - 1000 * 60 * 290).toISOString()
      },
      {
        role: 'assistant',
        content: 'Perfecto. Nuestro servicio de consultoría estratégica para empresas de manufactura incluye análisis de procesos, identificación de oportunidades de mejora, e implementación de soluciones Lean Manufacturing. El proyecto típico tiene una duración de 3-6 meses con un ROI promedio del 300%. ¿Le gustaría recibir una propuesta formal?',
        timestamp: new Date(Date.now() - 1000 * 60 * 285).toISOString()
      },
      {
        role: 'user',
        content: 'Sí, me interesa mucho. ¿Cuál sería el presupuesto estimado?',
        timestamp: new Date(Date.now() - 1000 * 60 * 280).toISOString()
      }
    ],
    output: {
      text: 'Para una empresa de su tamaño, el proyecto tendría un costo entre $45,000 y $75,000 USD, dependiendo del alcance específico. Incluye análisis inicial, plan de implementación, y seguimiento durante 6 meses. ¿Le parece bien si programamos una videollamada esta semana para discutir los detalles?'
    },
    metadata: {
      assistant_name: 'Asistente Teruya',
      call: {
        duration: 420,
        quality: 'excellent'
      }
    },
    createdAt: new Date(Date.now() - 1000 * 60 * 300).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 280).toISOString(),
    observations: [],
    scores: [
      {
        name: 'Oportunidad de venta',
        value: 0.96,
        source: 'auto',
        comment: 'Lead caliente - mostró interés en presupuesto',
        timestamp: new Date(Date.now() - 1000 * 60 * 280).toISOString()
      },
      {
        name: 'Profesionalismo',
        value: 0.94,
        source: 'auto',
        comment: 'Conversación profesional y estructurada',
        timestamp: new Date(Date.now() - 1000 * 60 * 280).toISOString()
      }
    ],
    totalCost: 0.0412,
    latency: 3.92,
    htmlPath: '/trace/trace-mock-003',
    externalId: null,
    success: true
  },
  {
    id: 'trace-mock-004',
    projectId: 'proj-001',
    name: 'Llamada fallida - timeout',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(), // 8 hours ago
    environment: 'production',
    tags: ['teruya', 'error'],
    bookmarked: false,
    release: 'v1.0.0',
    version: '1.0',
    userId: null,
    sessionId: 'session-abc-004',
    public: false,
    input: [
      {
        role: 'user',
        content: 'Hola',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString()
      }
    ],
    output: null,
    metadata: {
      assistant_name: 'Asistente Teruya',
      call: {
        duration: 5,
        quality: 'poor',
        error: 'Connection timeout'
      }
    },
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(),
    observations: [],
    scores: [],
    totalCost: 0.0008,
    latency: 0.12,
    htmlPath: '/trace/trace-mock-004',
    externalId: null,
    success: false
  },
  {
    id: 'trace-mock-005',
    projectId: 'proj-001',
    name: 'Llamada de seguimiento',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(), // 12 hours ago
    environment: 'production',
    tags: ['teruya', 'seguimiento'],
    bookmarked: false,
    release: 'v1.0.0',
    version: '1.0',
    userId: 'user-321',
    sessionId: 'session-abc-005',
    public: false,
    input: [
      {
        role: 'user',
        content: 'Hola, llamé ayer para consultar sobre sus servicios y quería confirmar la reunión',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString()
      },
      {
        role: 'assistant',
        content: 'Claro que sí. Déjeme verificar. ¿Puede proporcionarme su nombre o número de referencia?',
        timestamp: new Date(Date.now() - 1000 * 60 * 715).toISOString()
      },
      {
        role: 'user',
        content: 'Mi nombre es Juan Pérez, llamé ayer por la tarde',
        timestamp: new Date(Date.now() - 1000 * 60 * 710).toISOString()
      }
    ],
    output: {
      text: 'Perfecto, Sr. Pérez. Tengo su solicitud aquí. La reunión está confirmada para mañana a las 10:00 AM. Le enviaré un recordatorio por correo con el enlace de la videollamada. ¿Hay algo más en lo que pueda ayudarle?'
    },
    metadata: {
      assistant_name: 'Asistente Teruya',
      call: {
        duration: 95,
        quality: 'good'
      }
    },
    createdAt: new Date(Date.now() - 1000 * 60 * 720).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 710).toISOString(),
    observations: [],
    scores: [
      {
        name: 'Eficiencia',
        value: 0.89,
        source: 'auto',
        comment: 'Resolución rápida de consulta',
        timestamp: new Date(Date.now() - 1000 * 60 * 710).toISOString()
      }
    ],
    totalCost: 0.0121,
    latency: 1.65,
    htmlPath: '/trace/trace-mock-005',
    externalId: null,
    success: true
  }
];

/**
 * Genera una respuesta mock de traces con paginación
 */
export function generateMockTracesResponse(page: number = 1, limit: number = 20): TraceListResponse {
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedTraces = mockTraces.slice(startIndex, endIndex);

  return {
    success: true,
    data: paginatedTraces,
    meta: {
      page,
      limit,
      totalItems: mockTraces.length,
      totalPages: Math.ceil(mockTraces.length / limit)
    }
  };
}

/**
 * Obtiene un trace mock por ID
 */
export function getMockTraceById(traceId: string): LangfuseTrace | null {
  return mockTraces.find(trace => trace.id === traceId) || null;
}
